const MAX_PUBLIC_BOOKING_WARNING_LENGTH = 300;

const sanitizeWarning = (value) => {
  if (typeof value !== "string") return "";
  return value.trim().slice(0, MAX_PUBLIC_BOOKING_WARNING_LENGTH);
};

export const normalizeBookingNavigationState = (value) => ({
  bookingPending: value?.bookingPending === true,
  warning: sanitizeWarning(value?.warning),
});

export const buildBookingCompletionNavigationState = (response) => normalizeBookingNavigationState({
  bookingPending: response?.booking_pending === true || response?.appointment?.status === "pending",
  warning: response?.warning,
});

export const mergeBookingPendingState = (current, response) => {
  const appointment = response?.appointment || null;
  const bookingPending = response?.booking_pending === true || appointment?.status === "pending";
  const responseWarning = sanitizeWarning(response?.warning);

  return {
    bookingPending,
    warning: bookingPending ? (responseWarning || sanitizeWarning(current?.warning)) : "",
  };
};

const PUBLIC_BOOKING_PRESENTATIONS = {
  loading: {
    key: "loading",
    eyebrow: "予約情報を確認中",
    title: "予約状態を確認しています",
    description: "予約情報を読み込んでいます。",
    statusLabel: "読み込み中",
    tone: "neutral",
  },
  confirmed: {
    key: "confirmed",
    eyebrow: "予約受付完了",
    title: "予約が完了しました",
    description: "入力いただいた内容で予約を確定しました。",
    statusLabel: "予約確定",
    tone: "success",
  },
  pending: {
    key: "pending",
    eyebrow: "予約枠確保済み",
    title: "予約枠を確保しました",
    description: "ご希望の時間枠は確保済みです。Zoom会議URLの発行と予約確定を続けています。",
    statusLabel: "Zoom URL準備中",
    tone: "pending",
  },
  cancelled: {
    key: "cancelled",
    eyebrow: "予約キャンセル済み",
    title: "この予約はキャンセルされています",
    description: "この予約はキャンセル済みです。会議URLは利用できません。",
    statusLabel: "キャンセル済み",
    tone: "inactive",
  },
  rescheduled: {
    key: "rescheduled",
    eyebrow: "日程変更済み",
    title: "この予約は日程変更されています",
    description: "新しい日程の予約情報をご確認ください。この画面の会議URLは利用できません。",
    statusLabel: "日程変更済み",
    tone: "inactive",
  },
  failed: {
    key: "failed",
    eyebrow: "予約確定を確認できません",
    title: "予約状態の確認が必要です",
    description: "予約は確定していません。予約IDを添えて案内元へお問い合わせください。",
    statusLabel: "確認が必要",
    tone: "error",
  },
  unknown: {
    key: "unknown",
    eyebrow: "予約状態を確認できません",
    title: "予約状態の確認が必要です",
    description: "現在の予約状態を判定できません。予約IDを添えて案内元へお問い合わせください。",
    statusLabel: "状態不明",
    tone: "error",
  },
};

const normalizeAppointmentStatus = (value) => String(value || "").trim().toLowerCase();

export const getPublicBookingPresentation = ({ appointment, bookingPending = false } = {}) => {
  const status = normalizeAppointmentStatus(appointment?.status);

  if (!appointment) {
    return bookingPending ? PUBLIC_BOOKING_PRESENTATIONS.pending : PUBLIC_BOOKING_PRESENTATIONS.loading;
  }
  if (status === "confirmed") return PUBLIC_BOOKING_PRESENTATIONS.confirmed;
  if (status === "pending") return PUBLIC_BOOKING_PRESENTATIONS.pending;
  if (status === "cancelled" || status === "canceled") return PUBLIC_BOOKING_PRESENTATIONS.cancelled;
  if (status === "rescheduled") return PUBLIC_BOOKING_PRESENTATIONS.rescheduled;
  if (["failed", "error", "sync_error"].includes(status)) return PUBLIC_BOOKING_PRESENTATIONS.failed;
  return PUBLIC_BOOKING_PRESENTATIONS.unknown;
};

export const isConfirmedPublicBooking = (appointment) => (
  normalizeAppointmentStatus(appointment?.status) === "confirmed"
);

export const getPublicMeetingUrlLabel = ({ appointment, bookingPending }) => {
  const presentation = getPublicBookingPresentation({ appointment, bookingPending });
  if (presentation.key === "confirmed") {
    return appointment?.meeting_url || "会議URLは発行されていません。";
  }
  if (presentation.key === "pending") {
    return "Zoom会議URLを準備中です。確認メールはまだ送信されていません。";
  }
  if (presentation.key === "cancelled") return "キャンセル済みの予約では会議URLを表示しません。";
  if (presentation.key === "rescheduled") return "日程変更前の会議URLは表示しません。";
  if (presentation.key === "loading") return "読み込み中";
  return "予約が確定していないため、会議URLは表示しません。";
};

export const getSafePublicMeetingUrl = (value) => {
  try {
    const parsed = new URL(String(value || ""));
    return parsed.protocol === "https:" ? parsed.toString() : "";
  } catch (error) {
    return "";
  }
};
